import { defineComponent as _defineComponent } from 'vue'
import Button from '@/components/buttons/Button.vue';
import CloseButton from '@/components/buttons/dedicated/CloseButton.vue';
import LabelWithColour from '@/components/colour/LabelWithColour.vue';
import AreaSessionFormItem from '@/components/form/AreaSessionFormItem.vue';
import BreakDetailsItem from '@/components/form/BreakDetailsItem.vue';
import BottomSheet from '@/components/modal/BottomSheet.vue';
import i18n from '@/i18n';
import { Icon } from '@/lib/enum/Icon';
import { formatDateToTimeString } from '@/lib/input/timePickerFunctions';
import { Entity } from '@/lib/realtime/RealtimeEntity';
import { realtimeQuery } from '@/lib/realtime/weak/realtimeFunctions';
import { getBreakTypeIcon } from '@/lib/scheduledBreak/scheduledBreakFunctions';
import store from '@/store';
import {
  ShiftSessionFormType,
  ShiftSessionTypeEnum,
} from '@/types/shiftFormV2';
import {
  ScheduledBreakFormItemType,
  ShiftAreaSessionFormItemType,
} from '@/types/shiftSessionsForm';
import { getEntity } from '@/util/entityFunctions';
import { viewport } from '@/util/windowFunctions';
import useVuelidate from '@vuelidate/core';
import { computed } from 'vue';
import {
  CompanySettingTimeFormatEnum,
  ScheduledBreak,
} from '../../../../../api/v1';
import { dateTimeFormats } from '../../../../lang/dateTimeFormats';
import { addMinutes } from '../../../../util/dateArithmetic';

type ItemType =
  | {
      type: ShiftSessionTypeEnum.Break;
      data: ScheduledBreak;
    }
  | {
      type: ShiftSessionTypeEnum.Area;
      data: ShiftAreaSessionFormItemType;
    };


export default /*#__PURE__*/_defineComponent({
  __name: 'ShiftSessionsList',
  props: {
    date: null,
    breaks: null,
    areaSessions: null,
    shiftSessionToEdit: { default: null },
    shiftStartTime: null,
    shiftEndTime: null,
    shiftLocationId: null,
    disableEditSession: { type: Boolean, default: true }
  },
  emits: ["editItem", "updateItem", "removeBreak", "removeArea", "submit", "close"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'editItem', value: ShiftSessionFormType): void;
  (e: 'updateItem', value: ScheduledBreakFormItemType): void;
  (e: 'removeBreak', id: number): void;
  (e: 'removeArea', id: number): void;
  (e: 'submit'): void;
  (e: 'close'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { date: Date, breaks: ScheduledBreak[], areaSessions: ShiftAreaSessionFormItemType[], shiftSessionToEdit: ShiftSessionFormType | null, shiftStartTime: string, shiftEndTime: string, shiftLocationId: number, disableEditSession: boolean }





// use vuelidate state from child LocationForm.vue component
// https://vuelidate-next.netlify.app/advanced_usage.html#nested-validations
const v$ = useVuelidate();

const timezone = computed<string>(() => store.getters.timezone);
const timeFormat = computed<CompanySettingTimeFormatEnum>(
  () => store.getters['settings/timeFormat'],
);
const paidBreakName = computed<string>(() => {
  return (
    store.state.settings.scheduleSettings.paidBreakName ??
    i18n.tc('label.paidBreak')
  );
});
const unpaidBreakName = computed<string>(() => {
  return (
    store.state.settings.scheduleSettings.unpaidBreakName ??
    i18n.tc('label.unpaidBreak')
  );
});

// We need all shift area statuses as old shifts might have archived/deleted areas
const shiftAreas = realtimeQuery(Entity.ShiftArea).fetch();

const itemList = computed<ItemType[]>(() => {
  return [
    ...props.breaks.map((b) => ({
      type: ShiftSessionTypeEnum.Break,
      data: b,
    })),
    ...props.areaSessions.map((a) => ({
      type: ShiftSessionTypeEnum.Area,
      data: a,
    })),
  ].sort((a, b) => {
    if (!a.data.startsAt && !a.data.startTime) {
      return -1;
    }
    if (!b.data.startsAt && !b.data.startTime) {
      return 1;
    }
    return (
      (a.data.startsAt ?? a.data.startTime) -
      (b.data.startsAt ?? b.data.startTime)
    );
  });
});

const editItem = (item: ItemType) => {
  emit('editItem', {
    type: item.type,
    form:
      item.type === ShiftSessionTypeEnum.Break
        ? {
            ...item.data,
            breakStartTimeEnabled: !!item.data.startsAt,
            startTime: formatDateToTimeString(
              item.data.startsAt,
              timeFormat.value,
              timezone.value,
            ),
          }
        : item.data,
  } as ShiftSessionFormType);
};

const headerAction = (item: ItemType): void => {
  if (props.disableEditSession) {
    return;
  }
  if (!props.shiftSessionToEdit) {
    return editItem(item);
  }
  if (
    props.shiftSessionToEdit.type === item.type &&
    props.shiftSessionToEdit.form.id === item.data.id
  ) {
    return emit('close');
  }
};

return { __sfc: true,props, emit, v$, timezone, timeFormat, paidBreakName, unpaidBreakName, shiftAreas, itemList, editItem, headerAction, Button, CloseButton, LabelWithColour, AreaSessionFormItem, BreakDetailsItem, BottomSheet, Icon, getBreakTypeIcon, ShiftSessionTypeEnum, getEntity, viewport, dateTimeFormats, addMinutes }
}

})